import { type FormEvent, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { FieldError, Label, TextArea, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Loader } from "@/components/ui/loader";
import { getApiUrl } from "@/lib/api";
import { useAccessionReport } from "./ReportContext";

const getFileUrl = (
  accessionId: number,
  options?: {
    reason?: string;
    preliminary?: boolean;
    displayedTests?: number[];
    comment?: string;
  },
) => {
  const url = new URL(
    getApiUrl(`/api/pdf/preview/${accessionId}`),
    window.location.origin,
  );

  // url.searchParams.set("format", "html");
  if (options?.reason) {
    url.searchParams.set("reason", options.reason);
  }
  if (options?.preliminary) {
    url.searchParams.set("preliminary", "true");
  }
  if (options?.displayedTests) {
    url.searchParams.set(
      "displayedTests",
      JSON.stringify(options.displayedTests),
    );
  }
  if (options?.comment) {
    url.searchParams.set("comment", options.comment);
  }
  url.hash = "toolbar=0";
  return url.toString();
};

const useGetFileUrl = (
  accessionId: number,
  options?: {
    reason?: string;
    preliminary?: boolean;
    displayedTests?: number[];
    comment?: string;
  },
) => {
  return getFileUrl(accessionId, options);
};

export function ReportPreview() {
  const {
    accession,
    loading,
    setLoading,
    reason,
    status,
    displayedTests,
    comment,
  } = useAccessionReport();
  const ref = useRef<HTMLIFrameElement>(null);
  const urlRef = useRef<string>(undefined);

  const fileUrl = useGetFileUrl(accession.id, {
    reason,
    preliminary: status === "preliminary",
    displayedTests,
    comment,
  });

  const onLoad = () => {
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: I want to re-run on status change
  useEffect(() => {
    if (!loading && ref.current && urlRef.current !== fileUrl) {
      setLoading(true);
      urlRef.current = fileUrl;
      ref.current.contentWindow?.location.replace(fileUrl);
    }
  }, [fileUrl, loading, setLoading, status]);

  if (status === "corrected" && !reason) {
    return <PreviewReasonForm />;
  }

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center h-full max-w-3xl w-full border bg-accent rounded-md">
          <Loader className="size-8" />
        </div>
      )}
      <iframe
        ref={ref}
        title={accession.id.toString()}
        style={{
          maxWidth: 800,
          display: loading ? "none" : "block",
          borderRadius: 6,
        }}
        className="border"
        height="100%"
        width="100%"
        onLoad={onLoad}
      />
    </>
  );
}

function PreviewReasonForm() {
  const { reason, setReason, loading, status } = useAccessionReport();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const newReason = formData.get("reason") as string;

    if (reason !== newReason) {
      setReason(newReason);
    }
  };

  if (status !== "corrected") {
    return null;
  }

  return (
    <div className="p-6 w-full lg:max-w-lg border rounded-lg mt-6 max-h-full">
      <Form onSubmit={onSubmit}>
        <TextField isRequired name="reason">
          <Label>Reason for corrected report</Label>
          <TextArea
            className="resize-none"
            style={{ fieldSizing: "content" } as React.CSSProperties}
          />
          <FieldError />
        </TextField>
        <div className="flex justify-end">
          <Button
            className="mt-4"
            type="submit"
            isDisabled={loading}
            variant="outline"
          >
            Preview
          </Button>
        </div>
      </Form>
    </div>
  );
}
